import {
  findFieldValue,
  isBrowserElement,
  isHtmlFormElement,
  mergeDefined,
  normalizePrice,
  normalizeQuantity,
  readAttribute,
  trimNonEmpty,
  uniqueNames,
} from './extractor-utils';

export type ExtractedProductData = {
  productId?: string;
  productName?: string;
  price?: number;
  quantity?: number;
  currency?: string;
};

export type ProductExtractorOptions = {
  attributeMappings?: Record<string, string[]>;
  fieldMappings?: Record<string, string[]>;
};

const DEFAULT_PRODUCT_ID_ATTRS = [
  'data-product-id',
  'data-product',
  'data-sku',
  'data-item-id',
  'data-id',
] as const;

const DEFAULT_PRODUCT_NAME_ATTRS = [
  'data-product-name',
  'data-item-name',
] as const;

const DEFAULT_PRICE_ATTRS = [
  'data-product-price',
  'data-price',
  'data-value',
  'data-amount',
] as const;

const DEFAULT_QUANTITY_ATTRS = ['data-quantity', 'data-qty'] as const;

const DEFAULT_CURRENCY_ATTRS = ['data-currency'] as const;

const DEFAULT_PRODUCT_ID_FIELDS = [
  'productId',
  'product_id',
  'sku',
  'item_id',
] as const;

const DEFAULT_PRODUCT_NAME_FIELDS = [
  'productName',
  'product_name',
  'item_name',
] as const;

const DEFAULT_PRICE_FIELDS = ['price', 'product_price', 'item_price'] as const;

const DEFAULT_QUANTITY_FIELDS = ['quantity', 'qty'] as const;

const DEFAULT_CURRENCY_FIELDS = ['currency'] as const;

const PRODUCT_CONTAINER_SELECTOR = [
  '[data-product-id]',
  '[data-sku]',
  '[data-product]',
  '[data-item-id]',
  '[data-track]',
  '.product-card',
  '.product',
  '[data-product-card]',
].join(', ');

/**
 * Extrai dados de produto a partir de atributos explícitos ou fields
 * de produto (`productId`, `product_name`, `sku`…). Não lê campos de
 * customer (`name`, `creditcards_name`) nem `product_codename`.
 * Catalog/cart entram no CommerceResolver, não aqui.
 *
 * Prioridade de merge: elemento alvo → form mais próximo → container.
 * Price/quantity são normalizados para `number` (sem conversão automática para centavos).
 */
export class ProductExtractor {
  private readonly attributeMappings: Record<string, string[]>;

  private readonly fieldMappings: Record<string, string[]>;

  constructor(options: ProductExtractorOptions = {}) {
    this.attributeMappings = options.attributeMappings ?? {};
    this.fieldMappings = options.fieldMappings ?? {};
  }

  extractFromElement(element: Element | null): ExtractedProductData {
    if (!isBrowserElement(element)) return {};

    const fromSelf = this.extractFromScope(element);
    const form = element.closest('form');
    const fromForm = isHtmlFormElement(form) ? this.extractFromScope(form) : {};
    const container = this.findProductContainer(element);
    const fromContainer =
      container && container !== element && container !== form
        ? this.extractFromScope(container)
        : {};

    return mergeDefined<ExtractedProductData>(
      fromSelf,
      fromForm,
      fromContainer
    );
  }

  extractFromForm(form: HTMLFormElement | null): ExtractedProductData {
    if (!isHtmlFormElement(form)) return {};
    return this.extractFromScope(form);
  }

  extractFromContainer(container: Element | null): ExtractedProductData {
    if (!isBrowserElement(container)) return {};
    return this.extractFromScope(container);
  }

  private extractFromScope(root: Element): ExtractedProductData {
    const productId =
      readAttribute(
        root,
        this.attrsFor('productId', DEFAULT_PRODUCT_ID_ATTRS)
      ) ??
      findFieldValue(
        root,
        this.fieldsFor('productId', DEFAULT_PRODUCT_ID_FIELDS),
        {
          exactOnly: true,
        }
      );

    const productName =
      readAttribute(
        root,
        this.attrsFor('productName', DEFAULT_PRODUCT_NAME_ATTRS)
      ) ??
      findFieldValue(
        root,
        this.fieldsFor('productName', DEFAULT_PRODUCT_NAME_FIELDS),
        { exactOnly: true }
      );

    const priceRaw =
      readAttribute(root, this.attrsFor('price', DEFAULT_PRICE_ATTRS)) ??
      findFieldValue(root, this.fieldsFor('price', DEFAULT_PRICE_FIELDS), {
        exactOnly: true,
      });

    const quantityRaw =
      readAttribute(root, this.attrsFor('quantity', DEFAULT_QUANTITY_ATTRS)) ??
      findFieldValue(
        root,
        this.fieldsFor('quantity', DEFAULT_QUANTITY_FIELDS),
        {
          exactOnly: true,
        }
      );

    const currency =
      readAttribute(root, this.attrsFor('currency', DEFAULT_CURRENCY_ATTRS)) ??
      findFieldValue(
        root,
        this.fieldsFor('currency', DEFAULT_CURRENCY_FIELDS),
        {
          exactOnly: true,
        }
      );

    return mergeDefined<ExtractedProductData>({
      productId: trimNonEmpty(productId),
      productName: trimNonEmpty(productName),
      price: normalizePrice(priceRaw),
      quantity: normalizeQuantity(quantityRaw),
      currency: trimNonEmpty(currency),
    });
  }

  private attrsFor(key: string, defaults: readonly string[]): string[] {
    return uniqueNames(this.attributeMappings[key], defaults);
  }

  private fieldsFor(key: string, defaults: readonly string[]): string[] {
    return uniqueNames(this.fieldMappings[key], defaults);
  }

  private findProductContainer(element: Element): Element | null {
    // parentElement: o próprio elemento já é coberto por fromSelf;
    // closest no pai encontra card/container ancestral (ex.: botão com data-track).
    return element.parentElement?.closest(PRODUCT_CONTAINER_SELECTOR) ?? null;
  }
}
